import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const InvoiceContext = createContext();

export const useInvoice = () => {
  const context = useContext(InvoiceContext);
  if (!context) {
    throw new Error('useInvoice must be used within an InvoiceProvider');
  }
  return context;
};

// App version for data migration purposes
const APP_VERSION = '1.0.1'; // Version incremented
const APP_VERSION_KEY = 'invoiceAppVersion';

export const InvoiceProvider = ({ children }) => {
  const [invoices, setInvoices] = useState([]);
  const [settings, setSettings] = useState({
    invoiceNumber: 1,
    travelInvoiceNumber: 1,
    marketingInvoiceNumber: 1,
    excursionBookingNumber: 1,
    companyInfo: {
      name: '',
      address: '',
      phone: '',
      email: '',
      taxId: ''
    },
    logoUrl: 'https://01st.info/wp-content/uploads/2022/12/logo-v2.png',
    travelAgencyInfo: {
      name: '',
      email: '',
      phone: '',
      logoUrl: ''
    },
    colors: {
      primary: '#1e3a8a',
      secondary: '#6366f1',
      accent: '#ec4899',
      dark: '#0f172a',
      light: '#f8fafc'
    },
    stripePublishableKey: '' // Added Stripe key
  });
  const [savedProducts, setSavedProducts] = useState([]);
  const [savedCustomers, setSavedCustomers] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const isInitialLoad = useRef(true);
  const isSyncing = useRef(false); // Verrou de synchronisation côté client

  // --- Google Sheet Sync Logic ---
  const syncToSheet = useCallback(() => {
    const localApiUrl = '/api/google-script-proxy';

    if (isSyncing.current) {
      return;
    }
    
    isSyncing.current = true;

    const dataToSync = {
      invoices: JSON.parse(localStorage.getItem('invoices') || '[]'),
      settings: JSON.parse(localStorage.getItem('invoiceSettings') || '{}'),
      customers: JSON.parse(localStorage.getItem('savedCustomers') || '[]'),
      products: JSON.parse(localStorage.getItem('savedProducts') || '[]'),
    };

    if (!dataToSync.settings.companyInfo) {
        console.warn("Sync aborted: settings look incomplete.");
        isSyncing.current = false;
        return;
    }

    fetch(localApiUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'syncData', payload: dataToSync })
    })
    .then(response => response.json())
    .then(result => {
        if (result.status !== 'success') {
          console.error("Error from proxied Google Apps Script:", result.message);
        }
    }).catch(error => {
        console.error("Client-side error during proxied sync:", error);
    }).finally(() => {
        isSyncing.current = false;
    });
  }, []);

  const debouncedSync = useCallback(
    (() => {
        let timer;
        return (func) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func();
            }, 3000); // Debounce by 3 seconds
        };
    })(),
    []
  );

  // Effect for syncing data changes to Google Sheet
  useEffect(() => {
    if (isInitialLoad.current || !dataLoaded) {
      return;
    }
    debouncedSync(syncToSheet);
  }, [invoices, settings, savedCustomers, savedProducts, syncToSheet, dataLoaded, debouncedSync]);

  // Load data from localStorage on mount
  useEffect(() => {
    const loadData = () => {
      try {
        const savedInvoices = localStorage.getItem('invoices');
        const savedSettings = localStorage.getItem('invoiceSettings');
        const savedProductsList = localStorage.getItem('savedProducts');
        const savedCustomersList = localStorage.getItem('savedCustomers');

        if (savedInvoices) {
            const parsedInvoices = JSON.parse(savedInvoices);
            const updatedInvoices = parsedInvoices.map(inv => {
              if (inv.status) {
                return inv; // Status already exists, keep it.
              }
              // Status is missing, so set a default based on type.
              const invoiceType = inv.invoiceType || 'default';
              let defaultStatus = 'paid';
              if (invoiceType === 'travel' || invoiceType === 'marketing') {
                defaultStatus = 'unpaid';
              }
              return { ...inv, status: defaultStatus };
            });
            setInvoices(updatedInvoices);
        }
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          if (!parsedSettings.travelAgencyInfo) parsedSettings.travelAgencyInfo = { name: '', email: '', phone: '', logoUrl: '' };
          if (!parsedSettings.colors) parsedSettings.colors = { primary: '#1e3a8a', secondary: '#6366f1', accent: '#ec4899', dark: '#0f172a', light: '#f8fafc' };
          if (!parsedSettings.travelInvoiceNumber) parsedSettings.travelInvoiceNumber = 1;
          if (!parsedSettings.marketingInvoiceNumber) parsedSettings.marketingInvoiceNumber = 1;
          if (!parsedSettings.excursionBookingNumber) parsedSettings.excursionBookingNumber = 1;
          if (!parsedSettings.stripePublishableKey) parsedSettings.stripePublishableKey = '';
          if (!parsedSettings.logoUrl) parsedSettings.logoUrl = 'https://01st.info/wp-content/uploads/2022/12/logo-v2.png';
          setSettings(parsedSettings);
        }
        if (savedProductsList) setSavedProducts(JSON.parse(savedProductsList));
        if (savedCustomersList) setSavedCustomers(JSON.parse(savedCustomersList));
        
        setDataLoaded(true);
        setTimeout(() => {
          isInitialLoad.current = false;
        }, 500);
      } catch (error) {
        console.error('Error loading data from localStorage:', error);
      }
    };

    loadData();
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => { if (dataLoaded) localStorage.setItem('invoices', JSON.stringify(invoices)); }, [invoices, dataLoaded]);
  useEffect(() => { if (dataLoaded) localStorage.setItem('invoiceSettings', JSON.stringify(settings)); }, [settings, dataLoaded]);
  useEffect(() => { if (dataLoaded) localStorage.setItem('savedProducts', JSON.stringify(savedProducts)); }, [savedProducts, dataLoaded]);
  useEffect(() => { if (dataLoaded) localStorage.setItem('savedCustomers', JSON.stringify(savedCustomers)); }, [savedCustomers, dataLoaded]);

  const exportData = () => {
    try {
      const data = { invoices, settings, savedProducts, savedCustomers, version: APP_VERSION, exportDate: new Date().toISOString() };
      const dataStr = JSON.stringify(data, null, 2);
      const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
      const exportFileDefaultName = `invoice_app_backup_${new Date().toISOString().slice(0, 10)}.json`;
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      return true;
    } catch (error) {
      console.error('Error exporting data:', error);
      return false;
    }
  };

  const importData = (jsonData) => {
    try {
      const data = JSON.parse(jsonData);
      if (data.invoices) setInvoices(data.invoices);
      if (data.settings) setSettings(data.settings);
      if (data.savedProducts) setSavedProducts(data.savedProducts);
      if (data.savedCustomers) setSavedCustomers(data.savedCustomers);
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  };

  const getInvoiceNumberInfo = (invoiceType) => {
    switch (invoiceType) {
      case 'travel': return { number: settings.travelInvoiceNumber, prefix: 'TRV', fullNumber: `TRV-${settings.travelInvoiceNumber}` };
      case 'marketing': return { number: settings.marketingInvoiceNumber, prefix: 'MKT', fullNumber: `MKT-${settings.marketingInvoiceNumber}` };
      case 'excursion': return { number: settings.excursionBookingNumber, prefix: 'EXC', fullNumber: `EXC-${settings.excursionBookingNumber}` };
      default: return { number: settings.invoiceNumber, prefix: 'INV', fullNumber: `INV-${settings.invoiceNumber}` };
    }
  };

  const addInvoice = (invoice) => {
    const invoiceType = invoice.invoiceType || 'default';
    const numberInfo = getInvoiceNumberInfo(invoiceType);
    const invoiceNumber = invoice.invoiceNumber || invoice.bookingNumber || numberInfo.number;
    const fullNumber = `${numberInfo.prefix}-${invoiceNumber}`;

    // Determine default status based on invoice type
    let defaultStatus = 'paid'; // Default for 'default' and 'excursion'
    if (invoiceType === 'travel' || invoiceType === 'marketing') {
      defaultStatus = 'unpaid';
    }

    const newInvoice = { 
      ...invoice, 
      id: Date.now(), 
      number: invoiceNumber, 
      fullNumber: fullNumber, 
      prefix: numberInfo.prefix, 
      createdAt: new Date().toISOString(), 
      status: invoice.status || defaultStatus // Apply the new default logic
    };

    setInvoices(prev => [newInvoice, ...prev]);
    const nextNumber = isNaN(parseInt(invoiceNumber)) ? invoiceNumber : parseInt(invoiceNumber) + 1;
    switch (invoiceType) {
      case 'travel': setSettings(prev => ({ ...prev, travelInvoiceNumber: nextNumber })); break;
      case 'marketing': setSettings(prev => ({ ...prev, marketingInvoiceNumber: nextNumber })); break;
      case 'excursion': setSettings(prev => ({ ...prev, excursionBookingNumber: nextNumber })); break;
      default: setSettings(prev => ({ ...prev, invoiceNumber: nextNumber })); break;
    }
    return newInvoice;
  };

  const updateInvoice = (id, updatedInvoice) => {
    setInvoices(prev =>
      prev.map(inv => {
        if (inv.id === id) {
          const newNumber = updatedInvoice.number !== undefined ? updatedInvoice.number : inv.number;
          const prefix = updatedInvoice.prefix || inv.prefix || 'INV';
          const newFullNumber = `${prefix}-${newNumber}`;
          
          return { 
            ...inv, 
            ...updatedInvoice, 
            number: newNumber,
            fullNumber: newFullNumber,
            status: updatedInvoice.status || inv.status,
            updatedAt: new Date().toISOString()
          };
        }
        return inv;
      })
    );
  };

  const updateInvoiceStatus = (id, status) => {
    setInvoices(prev =>
      prev.map(inv =>
        inv.id === parseInt(id) ? { ...inv, status, updatedAt: new Date().toISOString() } : inv
      )
    );
  };

  const deleteInvoice = (id) => {
    setInvoices(prev => prev.filter(inv => inv.id !== id));
  };

  const saveProduct = (product) => {
    const exists = savedProducts.find(p => p.name === product.name && p.description === product.description);
    if (!exists) setSavedProducts(prev => [...prev, { ...product, id: Date.now() }]);
  };

  const saveCustomer = (customer) => {
    const exists = savedCustomers.find(c => c.name === customer.name && c.email === customer.email);
    if (!exists) setSavedCustomers(prev => [...prev, { ...customer, id: Date.now() }]);
  };

  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const value = { invoices, settings, savedProducts, savedCustomers, addInvoice, updateInvoice, deleteInvoice, saveProduct, saveCustomer, updateSettings, exportData, importData, setSavedCustomers, setSavedProducts, getInvoiceNumberInfo, dataLoaded, updateInvoiceStatus };

  return (
    <InvoiceContext.Provider value={value}>
      {children}
    </InvoiceContext.Provider>
  );
};